import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';

import {
  useCurrentUser,
  useEmailSettingsUpdate,
  useSignUpForWeeklyDigest,
  useSubscribeToNewsletter,
  useUnsubscribeFromNewsletter,
  useUnsubscribeFromWeeklyDigest,
} from '@hooks';
import { subscribe_path, upgrade_subscription_path } from '@routes';
import { DEFAULT_PRO_SUBSCRIBE_OFFER_CODE } from '@src/constants';
import { getImgixSrc } from '@utils/images';

import SubscriptionStatusCheckboxes from './SubscriptionStatusCheckbox';
import { WEEKLY_DIGEST_PROPS } from './constants';
import styles from './styles.module.scss';

interface EmailSettingOptionProps {
  accessLevel: number;
  author?: string;
  frequency?: string;
  newsletterId?: number;
  offerCode?: string;
  isPro?: boolean;
  isSubscribed: boolean;
  slug: string;
  subheader: string;
  title: string;
  checkForConfirmationModal: () => void;
}

const EmailSettingsOption = ({
  accessLevel,
  author,
  frequency,
  newsletterId,
  offerCode,
  isPro,
  isSubscribed,
  slug,
  subheader,
  title,
  checkForConfirmationModal,
}: EmailSettingOptionProps) => {
  const { currentUser } = useCurrentUser();
  const { mutate: subscribeToNewsletter } = useSubscribeToNewsletter(true);
  const { mutate: unsubscribeFromNewsletter } = useUnsubscribeFromNewsletter(true);
  const { mutate: signUpForWeeklyDigest } = useSignUpForWeeklyDigest({ skipRedirect: true });
  const { mutate: unsubscribeFromWeeklyDigest } = useUnsubscribeFromWeeklyDigest();
  const { mutate: updateEmailSettings } = useEmailSettingsUpdate();

  const triggerSubscriptionChange = useCallback(
    (articleNotificationType: string, isSubscribing: boolean) => {
      const newsletterAction = isSubscribing ? subscribeToNewsletter : unsubscribeFromNewsletter;

      if (newsletterId) {
        newsletterAction({ newsletter_id: newsletterId });
      } else {
        updateEmailSettings({ user: { article_notifications: articleNotificationType } });
      }
      checkForConfirmationModal();
    },
    [
      checkForConfirmationModal,
      newsletterId,
      subscribeToNewsletter,
      updateEmailSettings,
      unsubscribeFromNewsletter,
    ]
  );

  const triggerLeadTypeChange = useCallback(async () => {
    if (isSubscribed) {
      unsubscribeFromWeeklyDigest();
    } else {
      signUpForWeeklyDigest();
    }
  }, [isSubscribed, signUpForWeeklyDigest, unsubscribeFromWeeklyDigest]);

  const actionJSX = useMemo(() => {
    if (title === WEEKLY_DIGEST_PROPS.title) {
      return (
        <div
          className={styles.actionWrapper}
          onClick={() => {
            triggerLeadTypeChange();
          }}
        >
          {isSubscribed ? (
            <SubscriptionStatusCheckboxes
              altText="Subscribed"
              className={clsx(styles.actionLabel, styles.following)}
              displayText="Following"
              imgSrc={getImgixSrc({
                path: 'icons/checkbox_red.svg',
                isEnvDependent: false,
              })}
            />
          ) : (
            <SubscriptionStatusCheckboxes
              altText="Not Subscribed"
              className={styles.actionLabel}
              displayText="Subscribe"
              imgSrc={getImgixSrc({
                path: 'icons/small_subscribe.svg',
                isEnvDependent: false,
              })}
            />
          )}
        </div>
      );
    } else if (isSubscribed) {
      return (
        <div
          className={styles.actionWrapper}
          onClick={() => {
            triggerSubscriptionChange('none', false);
          }}
        >
          <SubscriptionStatusCheckboxes
            altText="Subscribed"
            className={clsx(styles.actionLabel, styles.following)}
            displayText="Following"
            imgSrc={getImgixSrc({
              path: 'icons/checkbox_red.svg',
              isEnvDependent: false,
            })}
          />
        </div>
      );
    } else if (isPro && !currentUser?.is_pro_subscriber) {
      return !currentUser.is_subscriber ? (
        <a href={subscribe_path({ offer: DEFAULT_PRO_SUBSCRIBE_OFFER_CODE })}>Sign up now</a>
      ) : (
        <a href={upgrade_subscription_path()}>Upgrade to Pro</a>
      );
    } else if (currentUser.access_level < accessLevel) {
      return <a href={subscribe_path({ offer: offerCode })}>Sign up for access</a>;
    } else {
      return (
        <div
          className={styles.actionWrapper}
          onClick={() => {
            triggerSubscriptionChange('all', true);
          }}
        >
          <SubscriptionStatusCheckboxes
            altText="Not Subscribed"
            className={styles.actionLabel}
            displayText="Subscribe"
            imgSrc={getImgixSrc({
              path: 'icons/small_subscribe.svg',
              isEnvDependent: false,
            })}
          />
        </div>
      );
    }
  }, [
    accessLevel,
    currentUser,
    isPro,
    isSubscribed,
    offerCode,
    title,
    triggerLeadTypeChange,
    triggerSubscriptionChange,
  ]);

  return (
    <div
      className={clsx(styles.optionContainer, { [styles.articleContainer]: !newsletterId })}
      id={slug}
    >
      <div className={styles.optionWrapper}>
        <div className="pb-16">
          <div className={styles.frequency}>{frequency}</div>
          <div className={styles.title}>{title}</div>
          {author && author != 'The Information Staff' && (
            <div className={styles.byLine}>by {author}</div>
          )}
          <div className={styles.subheader}>{subheader}</div>
        </div>
        <div className={clsx(styles.actionContainer, 'action')}>{actionJSX}</div>
      </div>
    </div>
  );
};

export default EmailSettingsOption;
